var _a, _b;
import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import React__default, { forwardRef, useContext, useRef, useState, useEffect, useMemo, cloneElement } from "react";
import { P as PropTypes } from "./index-qdNxdI8l.js";
import { G as GlobalHeadContext, c as getEnvVariable, i as getOfferCtaAdditionalProps } from "./OffersContext-BHJzoe2E.js";
import { H as HOST_PLATFORM, o as LINK_TYPES, A as ARIA_LABELS, S as SKELETON_VARIANTS, p as SOURCE_PREFIX, j as MKT_GRID_AREAS, a as MKT_CONTAINER_ID, f as MKT_OFFER_CONTAINER, M as MKT_CONTAINER_BREAKPOINT, q as CRITERIA_TYPE, B as BUTTON_MIN_WIDTH, F as FOCUS_OUTLINE_CLASS, O as OFFER_CTA_PREFIX } from "./index-B6R21tiH.js";
import { a as useSourceIdCookie, u as useOffersContext } from "./useSourceIdCookie-B39stm4z.js";
import { IconButton, Skeleton as Skeleton$1, Box, InputAdornment, InputLabel, FormHelperText, OutlinedInput, List, ListItem, ListItemIcon, styled as styled$2, FormControl, Select, MenuItem, useTheme, ClickAwayListener, Tooltip as Tooltip$1 } from "@mui/material";
import { styled } from "@mui/material/styles";
import { l as logoLarge, a as logoSmall } from "./logo-quickenloans-small-Gce_Lb86.js";
import { a as isWindowDefined } from "./index-CzdAPYVv.js";
import { Search, ChevronRight, Close } from "@mui/icons-material";
import { styled as styled$1 } from "@mui/system";
import { BREAKPOINT_DIMENSIONS } from "@quickenloans-ui-suite/design-system";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { B as Button } from "./Button-C3WhTBAE.js";
import InputAdornment$1 from "@mui/material/InputAdornment";
import TextFieldMUI from "@mui/material/TextField";
import CheckIcon from "@mui/icons-material/Check";
import Box$1 from "@mui/material/Box";
import { tooltipClasses } from "@mui/material/Tooltip";
const LogoImage$1 = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "contain",
  "&:hover": {
    cursor: "pointer"
  }
});
const convertHrefToUrl = (href, baseUrl) => {
  try {
    return new URL(href).href;
  } catch (e) {
    return new URL(href, baseUrl).href;
  }
};
const isOffSite = (hostname, href, target) => {
  var _a2;
  if (!hostname) return true;
  if (target === "_blank") return true;
  const url = convertHrefToUrl(href, ((_a2 = window == null ? void 0 : window.location) == null ? void 0 : _a2.href) || `https://${hostname}`);
  return !url.includes(hostname);
};
const getLocalPath = (href, property = null) => {
  const url = new URL(href, "https://www.quickenloans.com");
  if (property) return url[property];
  return `${url.pathname}${url.search}${url.hash}`;
};
const LinkResolver = forwardRef(({
  href,
  target = "_self",
  ...props
}, ref) => {
  const { LinkComponent: Link, uat, hostname, hostPlatform } = useContext(GlobalHeadContext);
  const linkType = hostPlatform === HOST_PLATFORM.GATSBY ? LINK_TYPES.GATSBY : LINK_TYPES.NEXT;
  const linkAttribute = {
    [linkType]: href
  };
  const linkProps = {
    ...props
  };
  if (!href || href === "#") {
    return /* @__PURE__ */ jsx("button", { ...linkProps });
  }
  linkProps.itemProp = "url";
  const uatHref = hostPlatform === HOST_PLATFORM.GATSBY ? "/eval-target" : href;
  const anchorHref = uat ? uatHref : href;
  const localPath = getLocalPath(anchorHref);
  linkAttribute[linkType] = uat ? uatHref : localPath;
  const isSameHostname = uat || hostname === getLocalPath(href, "hostname");
  let isSamePathname = false;
  if (isWindowDefined()) {
    isSamePathname = uat ? getLocalPath(window.location.href).toLowerCase().startsWith(localPath.toLowerCase()) : getLocalPath(window.location.href).toLowerCase() === localPath.toLowerCase();
  }
  const isOffSiteHref = uat ? false : !isWindowDefined() || isOffSite(hostname, anchorHref, target);
  let shouldRenderAnchor = Link === "a";
  if (isSameHostname && isSamePathname) shouldRenderAnchor = true;
  if (isOffSiteHref) shouldRenderAnchor = true;
  if (shouldRenderAnchor) {
    return /* @__PURE__ */ jsx("a", { ref, href: anchorHref, target, ...linkProps });
  }
  linkProps["data-type"] = "hostlink";
  return /* @__PURE__ */ jsx(Link, { ref, ...linkAttribute, ...linkProps, "data-name": "host-link" });
});
LinkResolver.propTypes = {
  /** The URL to navigate to */
  href: PropTypes.string,
  /** The target attribute for the link */
  target: PropTypes.string
};
function Logo({ showLarge, sx = {}, testId = "global-header-logo" }) {
  const { environment: currentEnv } = useContext(GlobalHeadContext);
  const logoSrc = showLarge ? logoLarge : logoSmall;
  const href = getEnvVariable({
    currentEnv,
    keyToSearch: "APP_BASE_URL"
  });
  return /* @__PURE__ */ jsx(LinkResolver, { id: "global-header-logo", href, children: /* @__PURE__ */ jsx(
    LogoImage$1,
    {
      src: logoSrc,
      alt: "Quicken Loans logo",
      title: "Quicken Loans logo",
      role: "img",
      "aria-label": ARIA_LABELS.LOGO,
      sx,
      "data-testid": testId
    }
  ) });
}
Logo.propTypes = {
  showLarge: PropTypes.bool,
  sx: PropTypes.object,
  testId: PropTypes.string
};
const SearchIcon = ({
  onClick = () => {
  },
  isPressedState,
  variant
}) => {
  return /* @__PURE__ */ jsx(
    Button,
    {
      type: "icon",
      variant,
      id: "SearchIconButton",
      "data-testid": "SearchIconButton",
      onClick,
      "aria-label": ARIA_LABELS.SEARCH_ICON,
      role: "button",
      isPressedState,
      iconLeft: /* @__PURE__ */ jsx(Search, {})
    }
  );
};
SearchIcon.propTypes = {
  onClick: PropTypes.func,
  isPressedState: PropTypes.bool,
  variant: PropTypes.oneOf(["small", "medium"])
};
const slugify = (text) => {
  const placeholder = String(text);
  if (placeholder === "") {
    return "";
  }
  const slug = placeholder.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "-").replace(/-{2,}/g, "-").replace(/^-|-$/g, "");
  return slug !== "" ? slug : "-";
};
const getFlexAlignment = (hasChildren, hasIcon) => ({
  justifyContent: hasIcon && hasChildren ? "space-between" : "center",
  alignItems: "center"
});
const getPadding = (hasChildren, hasIcon) => {
  const paddingMap = {
    "true-true": "6px 4px 6px 8px",
    "true-false": "8px 12px 8px 8px",
    "false-true": "8px 4px 8px 8px",
    "false-false": "8px"
  };
  const key = `${hasIcon}-${hasChildren}`;
  return paddingMap[key];
};
const IconWrapper = styled$1("div")({
  display: "inline-flex",
  justifyContent: "flex-end",
  alignItems: "center",
  flexGrow: 1
});
const IconSpan = styled$1("div")({
  display: "inline-flex",
  justifyContent: "flex-end",
  alignItems: "center",
  width: "20px",
  height: "20px",
  svg: {
    width: "100%",
    height: "100%"
  }
});
const Container = styled$1("div")(({
  theme,
  hasChildren = false,
  hasIcon = false,
  isActive = false,
  isForMainNav = false
}) => {
  var _a2, _b2, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o;
  const { link } = theme["global-head"].nav.desktop;
  return {
    ...getFlexAlignment(hasChildren, hasIcon),
    alignItems: "center",
    backgroundColor: "transparent",
    borderRadius: `${isForMainNav ? "0" : `${link.borderRadius}`}px`,
    boxSizing: "border-box",
    display: "inline-flex",
    flexShrink: 0,
    gap: `${link.gap}px`,
    height: `${link.height}px`,
    maxHeight: `${link.height}px`,
    justifyContent: "flex-start",
    outline: "none",
    padding: getPadding(hasChildren, hasIcon),
    textDecoration: "none",
    width: "100%",
    cursor: "pointer",
    WebkitTapHighlightColor: "transparent",
    /* Remove the ripple effect on mobile devices */
    ...isForMainNav ? {
      borderBottom: "2px solid transparent",
      transition: "border-bottom 0.3s ease, background-color 0.3s ease",
      "&:hover": {
        borderBottom: `2px solid ${(_a2 = theme.vars.palette.primary) == null ? void 0 : _a2[100]}`,
        span: {
          color: (_b2 = theme.vars.palette.primary) == null ? void 0 : _b2[100]
        }
      },
      ...isActive ? {
        borderBottom: `2px solid ${(_c = theme.vars.palette.primary) == null ? void 0 : _c[110]}`,
        span: {
          color: (_d = theme.vars.palette.primary) == null ? void 0 : _d[110]
        }
      } : {}
    } : {
      "@media (hover: hover)": {
        // allow hover styles on devices that support it
        "&:hover": {
          background: (_e = theme.vars.palette.primary) == null ? void 0 : _e[5],
          span: {
            color: (_f = theme.vars.palette.primary) == null ? void 0 : _f[100]
          },
          svg: {
            fill: (_g = theme.vars.palette.primary) == null ? void 0 : _g[110]
          }
        }
      },
      "&:active": {
        // preserve style when clicking on touch devices
        background: (_h = theme.vars.palette.primary) == null ? void 0 : _h[10],
        span: {
          color: (_i = theme.vars.palette.primary) == null ? void 0 : _i[110]
        },
        svg: {
          fill: (_j = theme.vars.palette.primary) == null ? void 0 : _j[110]
        }
      },
      ...isActive ? {
        background: (_k = theme.vars.palette.primary) == null ? void 0 : _k[10],
        span: {
          color: (_l = theme.vars.palette.primary) == null ? void 0 : _l[110]
        }
      } : {}
    },
    svg: {
      fill: isActive ? (_m = theme.vars.palette.primary) == null ? void 0 : _m[110] : (_n = theme.vars.palette.grey) == null ? void 0 : _n[80]
    },
    [theme.breakpoints.up(BREAKPOINT_DIMENSIONS.extra_large)]: {
      width: "auto"
    },
    a: {
      "&:active, &:visited": {
        color: (_o = theme.vars.palette.grey) == null ? void 0 : _o[80]
      }
    }
  };
});
const LinkWrapper = styled$1(forwardRef(({
  hasChildren,
  hasIcon,
  isActive,
  isForMainNav,
  theme,
  ...rest
}, ref) => /* @__PURE__ */ jsx(LinkResolver, { ref, ...rest })))(({ isActive = false, theme = {} }) => {
  var _a2, _b2;
  return {
    boxShadow: "none",
    color: isActive ? (_a2 = theme.vars.palette.primary) == null ? void 0 : _a2[110] : (_b2 = theme.vars.palette.grey) == null ? void 0 : _b2[80],
    fontFamily: theme.typography.fontFamily,
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: 0,
    textDecoration: "none",
    backgroundColor: "transparent",
    borderWidth: 0,
    fontSize: theme.typography.htmlFontSize,
    fontStyle: "normal",
    fontWeight: theme.typography.fontWeightRegular,
    lineHeight: theme.customSpacing.lineHeight,
    cursor: "pointer",
    span: {
      maxHeight: "37px",
      overflow: "hidden",
      textAlign: "left"
    }
  };
});
const LinkIcon = ({ icon: icon2, testId }) => /* @__PURE__ */ jsx(IconWrapper, { "data-testid": testId, children: /* @__PURE__ */ jsx(IconSpan, { children: icon2 }) });
const LinkTitle = ({ testId, title, ...rest }) => /* @__PURE__ */ jsx("span", { "data-testid": testId, ...rest, children: title });
const NavigationLinkContainer = ({ className, icon: icon2, onClick, sx, title, linkWrapperRef, href, ...rest }) => {
  const defaultTestId = slugify(`n-link-${title}`);
  const titleItemProp = (href == null ? void 0 : href.length) && href !== "#" ? "name" : null;
  return /* @__PURE__ */ jsxs(
    Container,
    {
      className,
      "data-testid": `${defaultTestId}-container`,
      onClick,
      sx,
      ...rest,
      children: [
        /* @__PURE__ */ jsxs(
          LinkWrapper,
          {
            ref: linkWrapperRef,
            "aria-selected": (rest == null ? void 0 : rest.isActive) ? "true" : "false",
            "data-testid": `${defaultTestId}-wrapper`,
            id: `${defaultTestId}-wrapper-${rest.isForMainNav ? "main" : "sub"}`,
            href,
            ...rest,
            children: [
              (rest == null ? void 0 : rest.hasIcon) && /* @__PURE__ */ jsx(LinkIcon, { icon: icon2, testId: `${defaultTestId}-icon-left` }),
              /* @__PURE__ */ jsx(LinkTitle, { testId: `${defaultTestId}-title`, title, itemProp: titleItemProp })
            ]
          }
        ),
        (rest == null ? void 0 : rest.hasChildren) && /* @__PURE__ */ jsx(LinkIcon, { icon: /* @__PURE__ */ jsx(ChevronRight, {}), testId: `${defaultTestId}-icon-right` })
      ]
    }
  );
};
const NavigationLink = ({
  className = "",
  hasChildren = false,
  href,
  target,
  icon: icon2,
  isActive = false,
  isForMainNav = true,
  onClick = () => {
  },
  title,
  sx = {}
}) => {
  const linkWrapperRef = useRef(!isWindowDefined() ? { click: () => {
  } } : null);
  const hasIcon = Boolean(icon2);
  const handleClick = (event) => {
    if (linkWrapperRef.current) {
      linkWrapperRef.current.click();
      return;
    }
    onClick(event);
  };
  return /* @__PURE__ */ jsx(
    NavigationLinkContainer,
    {
      className,
      hasChildren,
      hasIcon,
      href,
      target,
      icon: icon2,
      isActive,
      isForMainNav,
      onClick: handleClick,
      sx,
      title,
      linkWrapperRef
    }
  );
};
NavigationLink.propTypes = {
  /** Static className that will allow overriding CSS module styling */
  className: PropTypes.string,
  /** When set to true it add the chevron right icon */
  hasChildren: PropTypes.bool,
  /** The URL to navigate to. If not provided, the NavigationLink will render as a button */
  href: PropTypes.string,
  /** The target attribute for the link */
  target: PropTypes.string,
  /** Inline SVG or a component that renders to an inline SVG. Will either appear left of text or in the center if there is not text provided. */
  icon: PropTypes.element,
  /** When set to true the background and font color will be changed */
  isActive: PropTypes.bool,
  /** When set to true the border radius will be removed */
  isForMainNav: PropTypes.bool,
  onClick: PropTypes.func,
  /** The system prop that allows defining system overrides as well as additional CSS styles */
  sx: PropTypes.object,
  /** NavigationLink title */
  title: PropTypes.string.isRequired
};
const HamburgerIconStyled = styled$1("svg")({
  width: 24,
  height: 24,
  viewBox: "0 0 24 24",
  fill: "none",
  "&.open": {
    "& .top": {
      transform: "translate(0px, 5px) rotate(45deg)"
    },
    "& .middle": {
      transform: "scaleX(0)",
      opacity: 0
    },
    "& .bottom": {
      transform: "translate(0px, -5px) rotate(-45deg)"
    }
  }
});
const RectStyled = styled$1("rect")(({ theme }) => {
  var _a2;
  return {
    transformOrigin: "center",
    transformBox: "fill-box",
    width: 20,
    height: 2,
    x: 2,
    fill: (_a2 = theme.vars.palette.grey) == null ? void 0 : _a2[80],
    transition: "all 0.3s ease-in-out"
  };
});
const defaultStyles = {
  padding: 0,
  "&:focus-visible": {
    outline: "auto 1px black"
  }
};
const HamburgerButton = ({
  sx = {},
  isOpen = false,
  onClick = () => {
  },
  testId = "hamburger-button"
}) => {
  return /* @__PURE__ */ jsx(
    IconButton,
    {
      sx: { ...defaultStyles, ...sx },
      size: "small",
      id: "hamburger-button",
      "data-testid": testId,
      disableRipple: true,
      onClick,
      "aria-label": ARIA_LABELS.NAVIGATION_MENU,
      "aria-expanded": isOpen,
      role: "button",
      children: /* @__PURE__ */ jsxs(
        HamburgerIconStyled,
        {
          "data-testid": `${testId}-icon`,
          className: isOpen ? "open" : "",
          children: [
            /* @__PURE__ */ jsx(RectStyled, { y: "6.5", className: "top" }),
            /* @__PURE__ */ jsx(RectStyled, { y: "11.5", className: "middle" }),
            /* @__PURE__ */ jsx(RectStyled, { y: "16.5", className: "bottom" })
          ]
        }
      )
    }
  );
};
HamburgerButton.propTypes = {
  sx: PropTypes.object,
  isOpen: PropTypes.bool,
  onClick: PropTypes.func,
  testId: PropTypes.string
};
const StyledSkeleton = styled$1(Skeleton$1)(({ variant, sx }) => {
  const reverseAnimation = (sx == null ? void 0 : sx.reverseAnimation) ?? false;
  return {
    backgroundColor: "#d3d3d3",
    ...variant === SKELETON_VARIANTS.ROUNDED_RECT && {
      borderRadius: "100px"
    },
    ...variant === SKELETON_VARIANTS.ROUNDED && {
      borderRadius: "10px"
    },
    ...reverseAnimation && {
      opacity: 0.4,
      animation: "skeletonInvert 2s ease-in-out 0.5s infinite"
    },
    ...reverseAnimation && {
      "@keyframes skeletonInvert": {
        "0%": {
          opacity: 0.4
        },
        "50%": {
          opacity: 1
        },
        "100%": {
          opacity: 0.4
        }
      }
    }
  };
});
const Skeleton = ({ variant, ...props }) => {
  return /* @__PURE__ */ jsx(StyledSkeleton, { "data-testid": `${variant}-skeleton`, variant, ...props });
};
Skeleton.protoType = {
  variant: PropTypes.oneOf(Object.values(SKELETON_VARIANTS))
};
const getColors$1 = (theme) => {
  var _a2, _b2, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _A;
  return {
    active: (_c = (_b2 = (_a2 = theme == null ? void 0 : theme.vars) == null ? void 0 : _a2.palette) == null ? void 0 : _b2.primary) == null ? void 0 : _c[110],
    default: (_f = (_e = (_d = theme == null ? void 0 : theme.vars) == null ? void 0 : _d.palette) == null ? void 0 : _e.grey) == null ? void 0 : _f[60],
    defaultBackground: (_i = (_h = (_g = theme == null ? void 0 : theme.vars) == null ? void 0 : _g.palette) == null ? void 0 : _h.grey) == null ? void 0 : _i[0],
    disabled: (_l = (_k = (_j = theme == null ? void 0 : theme.vars) == null ? void 0 : _j.palette) == null ? void 0 : _k.grey) == null ? void 0 : _l[30],
    error: (_o = (_n = (_m = theme == null ? void 0 : theme.vars) == null ? void 0 : _m.palette) == null ? void 0 : _n.secondary) == null ? void 0 : _o[90],
    filled: (_r = (_q = (_p = theme == null ? void 0 : theme.vars) == null ? void 0 : _p.palette) == null ? void 0 : _q.grey) == null ? void 0 : _r[70],
    label: {
      active: (_u = (_t = (_s = theme == null ? void 0 : theme.vars) == null ? void 0 : _s.palette) == null ? void 0 : _t.primary) == null ? void 0 : _u[110],
      error: (_x = (_w = (_v = theme == null ? void 0 : theme.vars) == null ? void 0 : _v.palette) == null ? void 0 : _w.secondary) == null ? void 0 : _x[90]
    },
    supportingText: {
      active: (_A = (_z = (_y = theme == null ? void 0 : theme.vars) == null ? void 0 : _y.palette) == null ? void 0 : _z.grey) == null ? void 0 : _A[50]
    }
  };
};
const StyledBox$1 = styled(Box)(() => ({
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  flexShrink: 0,
  marginBottom: 20,
  width: "100%"
}));
const StyledAdornment = styled(({ hasPointer, isDisabled, ...rest }) => /* @__PURE__ */ jsx(InputAdornment, { ...rest }))(({ hasPointer, isDisabled }) => ({
  "&.MuiInputAdornment-root": {
    marginRight: 0,
    "svg, img": {
      flexShrink: 0,
      height: 24,
      width: 24
    },
    ...hasPointer ? { cursor: "pointer" } : {},
    ...isDisabled ? { opacity: 0.5 } : {}
  }
}));
const StyledLabel$1 = styled(InputLabel)(({ theme, state = "default" }) => {
  var _a2, _b2;
  const colors = getColors$1(theme);
  return {
    color: (colors == null ? void 0 : colors[state]) ?? colors.default,
    fontSize: 12,
    fontStyle: "normal",
    fontFamily: (_a2 = theme == null ? void 0 : theme.typography) == null ? void 0 : _a2.fontFamily,
    fontWeight: 600,
    lineHeight: (_b2 = theme == null ? void 0 : theme.customSpacing) == null ? void 0 : _b2.lineHeight,
    margin: "0 0 2px 17px",
    "&.Mui-disabled": {
      color: colors.disabled
    },
    "&.Mui-error": {
      color: colors.label.error
    },
    "&.Mui-focused": {
      color: colors.label.active
    }
  };
});
const StyledSupportingText$1 = styled(FormHelperText)(({ theme, state = "default" }) => {
  var _a2, _b2, _c;
  const colors = getColors$1(theme);
  return {
    color: (colors == null ? void 0 : colors[state]) ?? colors.default,
    fontSize: 12,
    fontStyle: "normal",
    fontFamily: (_a2 = theme == null ? void 0 : theme.typography) == null ? void 0 : _a2.fontFamily,
    fontWeight: (_b2 = theme == null ? void 0 : theme.typography) == null ? void 0 : _b2.fontWeightRegular,
    lineHeight: (_c = theme == null ? void 0 : theme.customSpacing) == null ? void 0 : _c.lineHeight,
    margin: "4px 0 0 17px",
    "&.Mui-disabled": {
      color: colors.disabled
    },
    "&.Mui-error": {
      color: colors.label.error
    },
    "&.Mui-focused": {
      color: colors.supportingText.active
    }
  };
});
const StyledInput = styled(
  ({ shouldReducePaddingRight = false, state = "default", theme, ...rest }) => /* @__PURE__ */ jsx(OutlinedInput, { ...rest })
)(({ theme, shouldReducePaddingRight, state }) => {
  var _a2, _b2, _c;
  const colors = getColors$1(theme);
  return {
    backgroundColor: colors.defaultBackground,
    borderRadius: 10,
    boxSizing: "border-box",
    color: (colors == null ? void 0 : colors[state]) ?? colors.default,
    flexShrink: 0,
    fontSize: 16,
    fontStyle: "normal",
    fontWeight: (_a2 = theme == null ? void 0 : theme.typography) == null ? void 0 : _a2.fontWeightRegular,
    gap: 8,
    height: 50,
    lineHeight: (_b2 = theme == null ? void 0 : theme.customSpacing) == null ? void 0 : _b2.lineHeight,
    padding: `0 ${shouldReducePaddingRight ? "12px" : "14px"} 0 14px`,
    maxWidth: 288,
    "& .MuiOutlinedInput-input": {
      boxSizing: "border-box",
      height: 50,
      padding: 0,
      width: 288,
      "&.Mui-disabled": {
        color: colors.disabled,
        WebkitTextFillColor: colors.disabled
      },
      "::placeholder": {
        fontFamily: (_c = theme == null ? void 0 : theme.typography) == null ? void 0 : _c.fontFamily,
        color: (colors == null ? void 0 : colors[state]) ?? colors.default,
        opacity: 1
      }
    },
    "&.Mui-disabled": {
      color: colors.disabled,
      "&:hover": {
        "& .MuiOutlinedInput-notchedOutline": {
          border: `1.5px solid ${colors.disabled}`
        }
      },
      "& .MuiOutlinedInput-notchedOutline": {
        border: `1.5px solid ${colors.disabled}`
      }
    },
    "&.Mui-error": {
      color: colors.filled,
      paddingRight: 14,
      "& .MuiOutlinedInput-notchedOutline": {
        border: `1.5px solid ${colors.error}`
      },
      "&:hover": {
        "& .MuiOutlinedInput-notchedOutline": {
          border: `1.5px solid ${colors.error}`
        }
      }
    },
    "&.Mui-focused": {
      color: colors.filled,
      paddingRight: 14,
      "& .MuiOutlinedInput-notchedOutline": {
        border: `1.5px solid ${colors.active}`
      },
      "&:hover": {
        "& .MuiOutlinedInput-notchedOutline": {
          border: `1.5px solid ${colors.active}`
        }
      }
    },
    "&:hover": {
      "& .MuiOutlinedInput-notchedOutline": {
        border: `1.5px solid ${(colors == null ? void 0 : colors[state]) ?? colors.default}`
      }
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: `1.5px solid ${(colors == null ? void 0 : colors[state]) ?? colors.default}`
    }
  };
});
const getAdornments = ({ clearAction, icon: icon2, iconAlt, isDisabled }) => {
  const adornment = {};
  if (icon2) {
    const renderIcon = () => {
      switch (typeof icon2) {
        case "function":
          return icon2();
        case "object":
          const Icon = icon2;
          return /* @__PURE__ */ jsx(Icon, {});
        case "string":
          return /* @__PURE__ */ jsx("img", { src: icon2, alt: iconAlt ?? "start adornment" });
        default:
          throw new Error("Invalid icon type");
      }
    };
    adornment.startAdornment = /* @__PURE__ */ jsx(
      StyledAdornment,
      {
        "data-testid": "textField__startAdornment",
        isDisabled,
        position: "start",
        variant: "outlined",
        children: renderIcon()
      }
    );
  }
  if (clearAction) {
    adornment.endAdornment = /* @__PURE__ */ jsx(StyledAdornment, { hasPointer: true, position: "end", children: /* @__PURE__ */ jsx(
      Close,
      {
        tabIndex: 0,
        "aria-hidden": "false",
        "data-testid": clearAction.dataTestId ?? "textField__endAdornment",
        onClick: clearAction.onClick,
        onKeyDown: (event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            clearAction.onClick();
          }
        }
      }
    ) });
  }
  return adornment;
};
const TextField$1 = ({
  autoComplete = "off",
  autoFocus = false,
  boxClassName = "",
  defaultValue = "",
  icon: icon2,
  iconAlt,
  id = "testId",
  inputClassName = "",
  isDisabled = false,
  label,
  labelClassName,
  onInputBlur = () => {
  },
  onInputChange = () => {
  },
  onInputFocused = () => {
  },
  placeholder,
  shouldBeShownAsError = false,
  supportingText = "",
  supportingTextClassName,
  sx = {},
  inputRef: passedRef,
  ...props
}) => {
  const internalRef = useRef(null);
  const inputRef = passedRef || internalRef;
  const [value, setValue] = useState(defaultValue);
  const [isFocused, setIsFocused] = useState(autoFocus);
  const [hasError, setHasError] = useState(shouldBeShownAsError || false);
  useEffect(() => {
    if (defaultValue !== value) {
      setValue(defaultValue);
    }
  }, [defaultValue]);
  useEffect(() => {
    setHasError(shouldBeShownAsError);
  }, [shouldBeShownAsError]);
  const handleChange = (event) => {
    var _a2;
    const value2 = (_a2 = event.target) == null ? void 0 : _a2.value;
    setValue(value2);
    onInputChange({ value: value2, inputRef });
  };
  const handleClear = () => {
    setValue("");
    onInputChange({ value: "", inputRef });
    setHasError(false);
    inputRef.current.focus();
  };
  const handleFocus = () => {
    setIsFocused(true);
    onInputFocused({ value, inputRef });
  };
  const handleBlur = () => {
    setIsFocused(false);
    onInputBlur({ value, inputRef });
  };
  const clearAction = !isDisabled && value ? { onClick: handleClear, dataTestId: `textField__clearButton--${id}` } : null;
  const inputState = (value == null ? void 0 : value.length) ? "filled" : "default";
  const shouldReducePaddingRight = (supportingText == null ? void 0 : supportingText.length) > 0 && (inputState !== "filled" || isDisabled);
  return /* @__PURE__ */ jsxs(StyledBox$1, { className: boxClassName, sx, children: [
    label && /* @__PURE__ */ jsx(
      StyledLabel$1,
      {
        className: labelClassName,
        "data-testid": `textField__label--${id}`,
        disabled: isDisabled,
        error: !isFocused && hasError,
        focused: isFocused,
        state: inputState,
        htmlFor: id,
        children: label
      }
    ),
    /* @__PURE__ */ jsx(
      StyledInput,
      {
        autoComplete,
        className: inputClassName,
        "data-testid": `textField__${id}`,
        disabled: isDisabled,
        error: hasError,
        id,
        inputMode: "text",
        inputRef,
        onBlur: handleBlur,
        onChange: handleChange,
        onFocus: handleFocus,
        placeholder,
        shouldReducePaddingRight,
        state: inputState,
        value,
        ...getAdornments({
          clearAction,
          icon: icon2,
          iconAlt,
          isDisabled
        }),
        ...props
      }
    ),
    supportingText && /* @__PURE__ */ jsx(
      StyledSupportingText$1,
      {
        className: supportingTextClassName,
        "data-testid": `textField__helperText--${id}`,
        disabled: isDisabled,
        error: hasError,
        focused: isFocused,
        state: inputState,
        children: supportingText
      }
    )
  ] });
};
TextField$1.propTypes = {
  autoFocus: PropTypes.bool,
  autoComplete: PropTypes.oneOf(["on", "off"]),
  /** Css/sass styles to be applied to the StyledBox */
  boxClassName: PropTypes.string,
  /** If exists, the value already stored for this input */
  defaultValue: PropTypes.string,
  /** The svg or the component to be rendered */
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),
  /** Alt used for the given svg */
  iconAlt: PropTypes.string,
  /** Will be used to identify the input */
  id: PropTypes.string,
  /** Css/sass styles to be applied to the StyledInput */
  inputClassName: PropTypes.string,
  isDisabled: PropTypes.bool,
  label: PropTypes.string,
  /** Css/sass styles to be applied to the StyledLabel */
  labelClassName: PropTypes.string,
  /** The following functions will be invoked when the events are triggered passing { value, inputRef } */
  onInputBlur: PropTypes.func,
  onInputChange: PropTypes.func,
  onInputFocused: PropTypes.func,
  /** Input basic placeholder */
  placeholder: PropTypes.string,
  /** Indicates if the input should be in an error state */
  shouldBeShownAsError: PropTypes.bool,
  /** Text to be shown as help for users */
  supportingText: PropTypes.string,
  /** Css/sass styles to be applied to the StyledSupportingText */
  supportingTextClassName: PropTypes.string,
  sx: PropTypes.object
};
const SourceIdLink = ({
  children,
  href,
  variant,
  ...props
}) => {
  const { sourceid } = useSourceIdCookie();
  const url = `${href}?sourceid=${sourceid || `${SOURCE_PREFIX}nav-start`}`;
  return /* @__PURE__ */ jsx(
    Button,
    {
      as: "a",
      type: "nav-filled",
      variant,
      href: url,
      ...props,
      children
    }
  );
};
SourceIdLink.propTypes = {
  /** Content or React Node */
  children: PropTypes.node.isRequired,
  /** URL */
  href: PropTypes.string.isRequired,
  /** Button variant */
  variant: PropTypes.oneOf(["small", "medium"]).isRequired
};
const ActualCheckIcon = ((_a = CheckIcon) == null ? void 0 : _a.default) ?? CheckIcon;
const ListStyled = styled(List)(({ theme }) => {
  var _a2;
  return {
    [`#${MKT_CONTAINER_ID} &`]: {
      display: "flex",
      flexDirection: "column",
      minWidth: "175px",
      padding: "0px",
      gap: theme.spacing(2),
      color: (_a2 = theme.vars.palette.grey) == null ? void 0 : _a2[80],
      gridArea: MKT_GRID_AREAS.OFFER_HIGHLIGHTS,
      li: {
        marginBottom: "0px",
        gap: theme.spacing(1)
      },
      [theme.containerQueries(MKT_OFFER_CONTAINER).up(MKT_CONTAINER_BREAKPOINT.desktop)]: {
        minWidth: "190px"
      }
    }
  };
});
const ListItemStyled = styled(ListItem)(({ theme }) => {
  return {
    [`#${MKT_CONTAINER_ID} &`]: {
      padding: "0",
      ...theme.typography.bodySmallRegular
    }
  };
});
const ListItemIconStyled = styled(ListItemIcon)(({ theme }) => {
  return {
    [`#${MKT_CONTAINER_ID} &`]: {
      minWidth: "0",
      alignSelf: "flex-start",
      alignItems: "center",
      padding: theme.spacing(0.25)
    }
  };
});
const CheckIconStyled = styled(ActualCheckIcon)(({ theme }) => {
  var _a2;
  return {
    [`#${MKT_CONTAINER_ID} &`]: {
      color: (_a2 = theme.vars.palette.success) == null ? void 0 : _a2[90],
      width: "18px",
      height: "18px"
    }
  };
});
function ProviderHighlights({
  highlights,
  clientId
}) {
  return /* @__PURE__ */ jsx(ListStyled, { "data-testid": `highlights-list-${clientId}`, children: ((highlights == null ? void 0 : highlights.slice(0, 3)) ?? []).map((item, index) => /* @__PURE__ */ jsxs(
    ListItemStyled,
    {
      "data-testid": `highlight-item-${clientId}-${index}`,
      children: [
        /* @__PURE__ */ jsx(ListItemIconStyled, { children: /* @__PURE__ */ jsx(CheckIconStyled, {}) }),
        item
      ]
    },
    index
  )) });
}
ProviderHighlights.propTypes = {
  highlights: PropTypes.array.isRequired,
  clientId: PropTypes.string.isRequired
};
const LogoImage = styled("img")({
  [`#${MKT_CONTAINER_ID} &`]: {
    width: "100%",
    height: "100%",
    objectFit: "contain"
  }
});
function OfferLogo({
  sx = {},
  logoUrl,
  clientId,
  clientName,
  nmls
}) {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      LogoImage,
      {
        sx,
        src: logoUrl,
        alt: `${clientName} logo`,
        "data-testid": `offer-logo-${clientId}`,
        loading: "lazy",
        itemProp: "logo"
      }
    ),
    /* @__PURE__ */ jsx("small", { "data-testid": `offer-${nmls}`, children: nmls ? nmls : "" })
  ] });
}
OfferLogo.propTypes = {
  sx: PropTypes.object,
  logoUrl: PropTypes.string.isRequired,
  clientId: PropTypes.string.isRequired,
  clientName: PropTypes.string.isRequired,
  nmls: PropTypes.string.isRequired
};
const OfferCriteriaContainer = styled(Box)(({ theme }) => {
  var _a2;
  return {
    [`#${MKT_CONTAINER_ID} & `]: {
      display: "flex",
      flexDirection: "row-reverse",
      justifyContent: "space-between",
      alignItems: "center",
      gap: theme.spacing(2),
      width: "100%",
      minWidth: "110px",
      gridArea: MKT_GRID_AREAS.OFFER_CRITERIA,
      "& h3, & p": {
        margin: 0,
        color: (_a2 = theme.vars.palette.grey) == null ? void 0 : _a2[80]
      },
      "& h3": {
        ...theme.typography.bodyLargeSemiBold
      },
      "& p": {
        ...theme.typography.bodySmallRegular
      },
      [theme.containerQueries(MKT_OFFER_CONTAINER).up(MKT_CONTAINER_BREAKPOINT.tablet)]: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        height: "auto",
        gap: theme.spacing(0.5),
        "& h3": {
          ...theme.typography.bodySmallSemiBold
        },
        "& p": {
          ...theme.typography.bodyXtraSmallRegular
        }
      },
      [theme.containerQueries(MKT_OFFER_CONTAINER).up(MKT_CONTAINER_BREAKPOINT.desktop)]: {
        "& h3": {
          ...theme.typography.h3SemiBold
        },
        "& p": {
          ...theme.typography.bodySmallRegular
        }
      }
    }
  };
});
const OfferCriteria = ({
  sx = {},
  criteria,
  clientId
}) => {
  const creditScoreCriteria = criteria.find((item) => item.type === CRITERIA_TYPE);
  if (!creditScoreCriteria) {
    return null;
  }
  const { min, max, description } = creditScoreCriteria;
  const criteriaValue = min && max ? `${min} – ${max}` : min || max;
  return /* @__PURE__ */ jsxs(OfferCriteriaContainer, { sx, "data-testid": `offer-criteria-${clientId}`, children: [
    /* @__PURE__ */ jsx("h3", { className: "criteria-value", "data-testid": `criteria-value-${clientId}`, children: criteriaValue }),
    /* @__PURE__ */ jsx("p", { "data-testid": `criteria-description-${clientId}`, children: description })
  ] });
};
OfferCriteria.propTypes = {
  sx: PropTypes.object,
  criteria: PropTypes.arrayOf(PropTypes.object).isRequired,
  clientId: PropTypes.string.isRequired
};
const OfferCtaContainer = styled(Box)(({ theme }) => {
  var _a2;
  return {
    [`#${MKT_CONTAINER_ID} &`]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: theme.spacing(1.5),
      gridArea: MKT_GRID_AREAS.OFFER_CTA,
      "small": {
        margin: 0,
        color: (_a2 = theme.vars.palette.grey) == null ? void 0 : _a2[70],
        textAlign: "center",
        ...theme.typography.bodyXtraSmallRegular
      },
      a: {
        "&:active, &:visited": {
          color: theme.vars.palette.grey[0]
        }
      }
    }
  };
});
const CtaButton = styled(Button, {
  shouldForwardProp: () => true
})(() => {
  return {
    [`#${MKT_CONTAINER_ID} &`]: {
      width: "100%",
      minWidth: BUTTON_MIN_WIDTH
    }
  };
});
const ActualOpenInNewIcon = ((_b = OpenInNewIcon) == null ? void 0 : _b.default) ?? OpenInNewIcon;
const OfferCta = ({
  isTestMode = false,
  marketingClickId,
  offer,
  sourceId,
  sx = {}
}) => {
  const {
    clientId,
    clientName,
    ctaButtonText,
    ctaAdditionalText = null
  } = offer;
  const { onClickHandler } = useOffersContext();
  const additionalText = ctaAdditionalText || `${OFFER_CTA_PREFIX} ${clientName}`;
  return /* @__PURE__ */ jsxs(OfferCtaContainer, { sx, "data-testid": `cta-section-${clientId}`, children: [
    /* @__PURE__ */ jsx(
      CtaButton,
      {
        className: FOCUS_OUTLINE_CLASS,
        variant: "small",
        type: "filled",
        iconRight: /* @__PURE__ */ jsx(ActualOpenInNewIcon, {}),
        "data-testid": `cta-button-${clientId}`,
        breakpoints: [
          {
            variant: "medium",
            size: MKT_CONTAINER_BREAKPOINT.tablet,
            container: MKT_OFFER_CONTAINER
          }
        ],
        ...getOfferCtaAdditionalProps({
          isTestMode,
          marketingClickId,
          offer,
          onClickHandler,
          sourceId
        }),
        children: ctaButtonText
      }
    ),
    /* @__PURE__ */ jsx("small", { "data-testid": `cta-subtext-${clientId}`, children: additionalText })
  ] });
};
OfferCta.propTypes = {
  sx: PropTypes.object,
  sourceId: PropTypes.string.isRequired,
  marketingClickId: PropTypes.string.isRequired,
  isTestMode: PropTypes.bool,
  offer: PropTypes.shape({
    clientId: PropTypes.string.isRequired,
    clientName: PropTypes.string.isRequired,
    ctaButtonText: PropTypes.string.isRequired,
    ctaUrl: PropTypes.string.isRequired,
    ctaUrlV2: PropTypes.string.isRequired,
    ctaAdditionalText: PropTypes.string
  }).isRequired
};
const getColors = (theme) => {
  var _a2, _b2, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _A, _B, _C, _D, _E, _F, _G;
  return {
    defaultBackground: (_c = (_b2 = (_a2 = theme == null ? void 0 : theme.vars) == null ? void 0 : _a2.palette) == null ? void 0 : _b2.grey) == null ? void 0 : _c[5],
    //#F2F2F2
    hover: (_f = (_e = (_d = theme == null ? void 0 : theme.vars) == null ? void 0 : _d.palette) == null ? void 0 : _e.grey) == null ? void 0 : _f[10],
    // hover color of select
    default: (_i = (_h = (_g = theme == null ? void 0 : theme.vars) == null ? void 0 : _g.palette) == null ? void 0 : _h.grey) == null ? void 0 : _i[70],
    //filled, #4D4D4D
    error: (_l = (_k = (_j = theme == null ? void 0 : theme.vars) == null ? void 0 : _j.palette) == null ? void 0 : _k.secondary) == null ? void 0 : _l[110],
    // #8B0E15
    active: (_o = (_n = (_m = theme == null ? void 0 : theme.vars) == null ? void 0 : _m.palette) == null ? void 0 : _n.primary) == null ? void 0 : _o[110],
    // #13496D
    disabled: (_r = (_q = (_p = theme == null ? void 0 : theme.vars) == null ? void 0 : _p.palette) == null ? void 0 : _q.grey) == null ? void 0 : _r[30],
    //#B3B3B3
    filled: (_u = (_t = (_s = theme == null ? void 0 : theme.vars) == null ? void 0 : _s.palette) == null ? void 0 : _t.grey) == null ? void 0 : _u[80],
    // #333333
    label: {
      active: (_x = (_w = (_v = theme == null ? void 0 : theme.vars) == null ? void 0 : _v.palette) == null ? void 0 : _w.primary) == null ? void 0 : _x[20]
    },
    menuOptions: {
      hover: (_A = (_z = (_y = theme == null ? void 0 : theme.vars) == null ? void 0 : _y.palette) == null ? void 0 : _z.primary) == null ? void 0 : _A[5],
      //#E9F4FB
      selectHover: (_D = (_C = (_B = theme == null ? void 0 : theme.vars) == null ? void 0 : _B.palette) == null ? void 0 : _C.primary) == null ? void 0 : _D[10],
      //#D3EAF8
      active: (_G = (_F = (_E = theme == null ? void 0 : theme.vars) == null ? void 0 : _E.palette) == null ? void 0 : _F.grey) == null ? void 0 : _G[20]
      //#ccccc
    }
  };
};
const StyledBox = styled$2(Box)(() => ({
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  flexShrink: 0,
  marginBottom: 20,
  width: "100%"
}));
const StyledFormControl = styled$2(FormControl)(({
  theme,
  state = "default",
  shrink = false
}) => {
  var _a2, _b2;
  const colors = getColors(theme);
  return {
    width: "261px",
    "& .MuiInputBase-root": {
      backgroundColor: colors.defaultBackground,
      borderRadius: "4px 4px 0px 0px",
      borderBottom: `1px solid ${colors.filled}`,
      "&.Mui-focused": {
        backgroundColor: colors.defaultBackground,
        borderBottom: `1px solid ${colors.active}`
      },
      "&.Mui-error": {
        borderBottom: `1px solid ${colors.error}`
      },
      "&.MuiSelect-icon, .MuiSelect-iconFilled": {
        color: colors.filled
      },
      "&.MuiFilledInput-input, .MuiSelect-filled": {
        color: colors.filled,
        fontSize: "16px",
        fontStyle: "normal",
        lineHeight: "160%",
        fontFamily: (_a2 = theme == null ? void 0 : theme.typography) == null ? void 0 : _a2.fontFamily,
        fontWeight: 400
      }
    },
    "& .MuiFormLabel-root ": {
      color: (colors == null ? void 0 : colors[state]) ?? (shrink ? colors.filled : colors.default),
      fontSize: shrink ? "12px" : "16px",
      fontStyle: "normal",
      fontFamily: (_b2 = theme == null ? void 0 : theme.typography) == null ? void 0 : _b2.fontFamily,
      lineHeight: shrink ? "130%" : "160%",
      fontWeight: 400,
      "&.Mui-focused": {
        color: colors.label.active,
        fontSize: "12px",
        lineHeight: "130%"
      },
      "&.Mui-error": {
        color: colors.error
      }
    },
    "&:hover": {
      "& .MuiInputBase-root": {
        backgroundColor: colors.hover
      },
      "& .MuiFormLabel-root": {
        color: colors.filled
      }
    },
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      width: "327px"
    }
  };
});
const StyledLabel = styled$2(InputLabel)(({
  theme,
  state = "default",
  shrink = false
}) => {
  const colors = getColors(theme);
  return {
    fontWeight: "400",
    color: (colors == null ? void 0 : colors[state]) ?? (shrink ? colors.filled : colors.default),
    fontSize: shrink ? "12px" : "16px",
    fontStyle: "normal",
    lineHeight: shrink ? "130%" : "160%",
    margin: "4px",
    "&.Mui-focused": {
      fontSize: "12px",
      lineHeight: "130%"
    }
  };
});
const getDefaultMenuProps = (theme) => {
  var _a2;
  return {
    MenuListProps: {
      style: {
        padding: "0px",
        paddingTop: "4px"
      }
    },
    slotProps: {
      list: {
        style: {
          padding: "0px",
          paddingTop: "4px"
        }
      },
      paper: {
        elevation: 2,
        style: {
          borderRadius: ((_a2 = theme.shape) == null ? void 0 : _a2.borderRadius) ?? 8,
          overflowY: "auto",
          maxHeight: "300px"
        }
      },
      backdrop: {
        style: {
          opacity: 0
        }
      }
    },
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "left"
    },
    transformOrigin: {
      vertical: "top",
      horizontal: "left"
    }
  };
};
const StyledSelectBase = styled$2(Select)(({ theme }) => {
  const colors = getColors(theme);
  return {
    width: "261px",
    height: "61px",
    padding: "8px 16px",
    borderRadius: "4px 4px 0px 0px",
    backgroundColor: colors.defaultBackground,
    borderBottom: `1px solid ${colors.filled}`,
    "&.Mui-focused": {
      backgroundColor: colors.defaultBackground,
      borderBottom: `1px solid ${colors.active}`
    },
    "&.Mui-error": {
      borderBottom: `1px solid ${colors.error}`
    },
    "& .MuiSelect-select": {
      padding: "0px",
      paddingTop: "15px"
    },
    [theme.breakpoints.down(theme.breakpoints.values.md)]: {
      width: "327px"
    }
  };
});
const StyledMenuItem = styled$2(MenuItem)(({ theme }) => {
  const colors = getColors(theme);
  return {
    padding: "12px 16px",
    color: colors.filled,
    foregroundColor: colors.filled,
    "&:hover": {
      color: colors.filled,
      backgroundColor: colors.defaultBackground
      // Normal hover
    },
    "&.Mui-selected": {
      color: colors.filled,
      backgroundColor: colors.menuOptions.hover,
      // Selected
      "&:hover": {
        color: colors.filled,
        backgroundColor: colors.menuOptions.selectHover
        // Selected and hover
      }
    },
    "&:active": {
      backgroundColor: colors.menuOptions.active,
      color: colors.filled
    }
  };
});
const StyledSelect = React__default.forwardRef(function StyledSelect2(props, ref) {
  const theme = useTheme();
  const { MenuProps, ...rest } = props;
  return /* @__PURE__ */ jsx(
    StyledSelectBase,
    {
      ref,
      MenuProps: {
        ...getDefaultMenuProps(theme),
        ...MenuProps
      },
      ...rest
    }
  );
});
const StyledSupportingText = styled$2(FormHelperText)(({ theme }) => {
  var _a2, _b2;
  const colors = getColors(theme);
  return {
    marginTop: 4,
    fontSize: "14px",
    fontStyle: "normal",
    lineHeight: "160%",
    fontFamily: (_a2 = theme == null ? void 0 : theme.typography) == null ? void 0 : _a2.fontFamily,
    fontWeight: (_b2 = theme == null ? void 0 : theme.typography) == null ? void 0 : _b2.fontWeightRegular,
    color: colors == null ? void 0 : colors.default,
    "&.Mui-error": {
      color: colors.error
    }
  };
});
const Dropdown = ({
  autoFocus = false,
  id = "testId",
  label,
  open = false,
  labelClassName = "",
  boxClassName = "",
  inputClassName = "",
  options,
  value,
  shouldBeShownAsError = false,
  errorMessage = "",
  supportingText = "",
  supportingTextClassName,
  disabled = false,
  labelId,
  onInputChange = () => {
  },
  onInputBlur = () => {
  },
  onInputFocused = () => {
  },
  sx = {},
  ...props
}) => {
  const inputRef = useRef(null);
  const [selectedValue, setSelectedValue] = useState(value || "");
  const [isMenuOpen, setIsMenuOpen] = useState(open);
  const [isFocused, setIsFocused] = useState(autoFocus);
  const [hasError, setHasError] = useState(shouldBeShownAsError || false);
  useEffect(() => {
    setHasError(shouldBeShownAsError);
  }, [shouldBeShownAsError]);
  const handleChange = (event) => {
    const value2 = event.target;
    setSelectedValue(value2 == null ? void 0 : value2.value);
    onInputChange({ value: value2, inputRef });
    setIsFocused(false);
    setHasError(false);
  };
  const handleFocus = () => {
    setIsFocused(true);
    onInputFocused({ selectedValue, inputRef });
  };
  const handleBlur = () => {
    setIsFocused(false);
    onInputBlur({ selectedValue, inputRef });
  };
  const handleOpen = (event) => {
    setIsMenuOpen(!isMenuOpen);
  };
  const handleClose = () => {
    setIsMenuOpen(false);
    setIsFocused(false);
  };
  const inputState = (selectedValue == null ? void 0 : selectedValue.length) ? "filled" : "default";
  const textToDisplay = hasError && errorMessage ? errorMessage : supportingText;
  return /* @__PURE__ */ jsx(StyledBox, { className: boxClassName, sx, children: /* @__PURE__ */ jsxs(
    StyledFormControl,
    {
      variant: "filled",
      state: inputState,
      shrink: isFocused || !!selectedValue,
      error: !isFocused && hasError,
      children: [
        label && /* @__PURE__ */ jsx(
          StyledLabel,
          {
            id: labelId,
            htmlFor: id,
            "data-testid": `dropdown__label--${labelId}`,
            className: labelClassName,
            error: !isFocused && hasError,
            focused: isFocused,
            shrink: isFocused || !!selectedValue,
            tabIndex: isMenuOpen ? 0 : -1,
            state: inputState,
            children: label
          }
        ),
        /* @__PURE__ */ jsx(
          StyledSelect,
          {
            open: isMenuOpen,
            id,
            labelId,
            name: id,
            autoFocus,
            inputRef,
            variant: "filled",
            value: selectedValue,
            onBlur: handleBlur,
            onChange: handleChange,
            onFocus: handleFocus,
            disabled,
            className: inputClassName,
            displayEmpty: true,
            error: hasError,
            state: inputState,
            "data-testid": `dropdown__${id}`,
            onOpen: handleOpen,
            onClose: handleClose,
            tabIndex: isMenuOpen ? 0 : -1,
            ...props,
            children: options.map((option) => /* @__PURE__ */ jsx(
              StyledMenuItem,
              {
                value: option.value,
                tabIndex: isMenuOpen ? 0 : -1,
                children: option.label
              },
              option.value
            ))
          }
        ),
        !isMenuOpen && textToDisplay && /* @__PURE__ */ jsx(
          StyledSupportingText,
          {
            className: supportingTextClassName,
            focused: isFocused,
            error: hasError,
            state: inputState,
            tabIndex: -1,
            "data-testid": `dropdown__supportingText--${id}`,
            children: textToDisplay
          }
        )
      ]
    }
  ) });
};
Dropdown.propTypes = {
  autoFocus: PropTypes.bool,
  open: PropTypes.bool,
  boxClassName: PropTypes.string,
  /** Will be used to identify the input */
  id: PropTypes.string,
  labelId: PropTypes.string,
  /** Css/sass styles to be applied to the StyledSelect */
  inputClassName: PropTypes.string,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  /** If exists, the value already stored for this input */
  value: PropTypes.string,
  /** Css/sass styles to be applied to the StyledLabel */
  labelClassName: PropTypes.string,
  /** The following functions will be invoked when the events are triggered passing { value, inputRef } */
  onInputBlur: PropTypes.func,
  onInputChange: PropTypes.func,
  onInputFocused: PropTypes.func,
  /** Indicates if the input should be in an error state */
  shouldBeShownAsError: PropTypes.bool,
  /** Text to be shown as help for users */
  supportingText: PropTypes.string,
  /** Css/sass styles to be applied to the StyledSupportingText */
  supportingTextClassName: PropTypes.string,
  errorMessage: PropTypes.string,
  sx: PropTypes.object
};
const StyledTextField = styled$2(Box$1)(({ theme }) => {
  var _a2, _b2, _c, _d;
  return console.log(theme, "theme in StyledTextField"), {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "& .MuiFormControl-root": {
      minWidth: 250
    },
    "& .MuiFormLabel-root": {
      paddingLeft: 5,
      ...theme.typography.bodySmallRegular
    },
    '& .MuiInputLabel-root[data-shrink="true"]': {
      ...theme.typography.bodyXtraSmallRegularMobile
    },
    "& .MuiInputBase-root.MuiFilledInput-root": {
      padding: theme == null ? void 0 : theme.spacing(0, 2, 0, 2)
    },
    "& .MuiInputBase-input": {
      padding: theme == null ? void 0 : theme.spacing(3, 0, 1, 0),
      color: (_b2 = (_a2 = theme == null ? void 0 : theme.palette) == null ? void 0 : _a2.grey) == null ? void 0 : _b2[70],
      ...theme.typography.bodySmallRegular
    },
    "& .MuiFormHelperText-root": {
      margin: 0,
      marginTop: 4,
      padding: theme == null ? void 0 : theme.spacing(0, 2),
      ...theme.typography.bodyXtraSmallRegular
    },
    "& .MuiInputAdornment-positionEnd": {
      cursor: "pointer",
      margin: 0
    },
    "& .MuiInputAdornment-root.MuiInputAdornment-positionStart": {
      margin: theme == null ? void 0 : theme.spacing(2, 0, 0, 0),
      marginLeft: -5,
      color: (_d = (_c = theme == null ? void 0 : theme.palette) == null ? void 0 : _c.grey) == null ? void 0 : _d[70],
      ...theme.typography.bodySmallRegular
    },
    "& .MuiSvgIcon-root": {
      width: 24,
      height: 24,
      ...theme.typography.bodySmallRegular
    }
  };
});
const TextField = ({
  autoComplete = "off",
  autoFocus = false,
  type = "text",
  label = "Label",
  disabled = false,
  helperText = "Supportive Text",
  onInputFocused = () => {
  },
  onInputBlur = () => {
  },
  onClearClick = () => {
  },
  defaultValue = "",
  onChange,
  errorText = "",
  showTrailingIcon = false,
  showClearIcon = false,
  showLeadingIcon = false,
  showPlaceholder = false,
  placeholder = "",
  shouldBeShownAsError = false,
  icon: icon2,
  iconAlt,
  id = "testId",
  boxClassName = "",
  textfieldClassName = "",
  sx = {},
  inputRef: passedRef,
  error = false,
  ...props
}) => {
  const internalRef = useRef(null);
  const inputRef = passedRef || internalRef;
  const [value, setValue] = useState(defaultValue);
  const [hasError, setHasError] = useState(shouldBeShownAsError || false);
  const [isFocused, setIsFocused] = useState(autoFocus);
  useEffect(() => {
    setValue(defaultValue);
    setHasError(error || shouldBeShownAsError);
  }, [defaultValue, shouldBeShownAsError, error]);
  const handleChange = (event) => {
    const newValue = event.target.value;
    setValue(newValue);
    if (onChange) {
      onChange(event, inputRef);
    }
  };
  const handleClear = () => {
    onClearClick();
    setValue("");
    if (onChange) {
      onChange({ target: { value: "" } }, inputRef);
    }
    setHasError(false);
  };
  const handleFocus = () => {
    setIsFocused(true);
    onInputFocused({ value, inputRef });
  };
  const handleBlur = () => {
    setIsFocused(false);
    onInputBlur({ value, inputRef });
  };
  const getIcon = useMemo(() => {
    return ({ icon: icon22, iconAlt: iconAlt2 }) => {
      if (!icon22) {
        return null;
      }
      const renderIcon = () => {
        switch (typeof icon22) {
          case "function":
            return icon22();
          case "object":
            const Icon = icon22;
            return /* @__PURE__ */ jsx(Icon, { color: "action" });
          case "string":
            return /* @__PURE__ */ jsx("img", { src: icon22, alt: iconAlt2 ?? "start adornment" });
          default:
            throw new Error("Invalid icon type");
        }
      };
      return renderIcon();
    };
  }, []);
  return /* @__PURE__ */ jsx(StyledTextField, { className: boxClassName, sx, children: /* @__PURE__ */ jsx(
    TextFieldMUI,
    {
      id,
      className: textfieldClassName,
      "data-testid": `textField__label--${id}`,
      type,
      disabled,
      focused: isFocused,
      onFocus: handleFocus,
      onBlur: handleBlur,
      label,
      variant: "filled",
      error: hasError,
      autoComplete,
      helperText: hasError ? errorText : helperText,
      value,
      onChange: handleChange,
      inputRef,
      placeholder: showPlaceholder ? placeholder : "",
      slotProps: {
        input: {
          startAdornment: showLeadingIcon ? /* @__PURE__ */ jsx(InputAdornment$1, { position: "start", id: "leading-icon", "data-testid": `leading-icon--${id}`, children: getIcon({ icon: icon2, iconAlt }) }) : null,
          endAdornment: showTrailingIcon ? showClearIcon ? /* @__PURE__ */ jsx(InputAdornment$1, { position: "end", id: "clear-icon", "data-testid": `clear-icon--${id}`, onClick: handleClear, children: getIcon({ icon: icon2, iconAlt }) }) : /* @__PURE__ */ jsx(InputAdornment$1, { position: "end", id: "trailing-icon", "data-testid": `trailing-icon--${id}`, children: getIcon({ icon: icon2, iconAlt }) }) : null
        }
      },
      ...props
    }
  ) });
};
TextField.propTypes = {
  autoComplete: PropTypes.oneOf(["on", "off"]),
  label: PropTypes.string,
  type: PropTypes.string,
  disabled: PropTypes.bool,
  helperText: PropTypes.string,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func,
  errorText: PropTypes.string,
  showleadingIcon: PropTypes.bool,
  showPlaceholder: PropTypes.bool,
  placeholder: PropTypes.string,
  showClearIcon: PropTypes.bool,
  shouldBeShownAsError: PropTypes.bool,
  icon: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.object,
    PropTypes.string
  ]),
  iconAlt: PropTypes.string,
  id: PropTypes.string,
  sx: PropTypes.object,
  boxClassName: PropTypes.string,
  textfieldClassName: PropTypes.string,
  showTrailingIcon: PropTypes.bool,
  focused: PropTypes.bool,
  onInputFocused: PropTypes.func,
  onInputBlur: PropTypes.func,
  onClearClick: PropTypes.func
};
const IconRenderer = ({ icon: IconComponent }) => {
  if (!IconComponent) {
    return /* @__PURE__ */ jsx(Fragment, {});
  }
  if (typeof IconComponent === "string") {
    return /* @__PURE__ */ jsx("img", { src: icon, alt: "start adornment" });
  }
  return /* @__PURE__ */ jsx(IconComponent, {});
};
IconRenderer.propTypes = {
  icon: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.elementType
  ])
};
const TooltipHelper = styled$2("span")`
    overflow: hidden;
`;
const getTooltipStyles = () => ({
  [`&.${tooltipClasses.popper} .${tooltipClasses.tooltip}`]: {
    borderRadius: "var(--radius-m, 0.5rem)",
    border: "1px solid var(--stroke-secondary, #E6E6E6)",
    background: "var(--background-primary, #FFF)",
    boxShadow: "0px 8px 24px 0px rgba(51, 51, 51, 0.05)",
    display: "flex",
    maxWidth: "20.4375rem",
    maxHeight: "40dvh",
    padding: "var(--gap-400, 2rem)",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "var(--gap-200, 1rem)",
    color: "var(--color-Grey-60, #666)",
    fontFamily: 'var(--font-Family-Myriad-Pro, "myriad-pro")',
    fontSize: "var(--font-Size-s, 1rem)",
    fontStyle: "normal",
    fontWeight: "var(--font-Weight-regular, 400)",
    lineHeight: "160%",
    "@media (min-width: 640px)": {
      maxWidth: "36rem"
    }
  },
  [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]: {
    marginTop: "8px"
  },
  [`&.${tooltipClasses.popper}[data-popper-placement*="top"] .${tooltipClasses.tooltip}`]: {
    marginBottom: "8px"
  },
  [`&.${tooltipClasses.popper}[data-popper-placement*="right"] .${tooltipClasses.tooltip}`]: {
    marginLeft: "8px"
  },
  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
    marginRight: "8px"
  }
});
const Tooltip = ({
  arrow = false,
  children,
  className = "",
  content,
  disableHoverListener = true,
  disableTouchListener = true,
  enterDelay = 0,
  leaveDelay = 300,
  size = "small",
  testId = "tooltip-id",
  ...rest
}) => {
  const [open, setOpen] = useState(false);
  const handleTooltipState = (value) => () => setOpen(value);
  const Title = () => /* @__PURE__ */ jsx(TooltipHelper, { children: content });
  const overwritenChildren = cloneElement(children, {
    onClick: handleTooltipState(true)
  });
  const tooltipCssClasses = {
    tooltip: className
  };
  return /* @__PURE__ */ jsx(ClickAwayListener, { onClickAway: handleTooltipState(false), children: /* @__PURE__ */ jsx(
    Tooltip$1,
    {
      arrow,
      open,
      onOpen: handleTooltipState(true),
      onClose: handleTooltipState(false),
      title: /* @__PURE__ */ jsx(Title, {}),
      "data-testid": testId,
      placement: "auto",
      disableHoverListener,
      disableTouchListener,
      ...!disableHoverListener && { enterDelay, leaveDelay },
      classes: tooltipCssClasses,
      slotProps: {
        popper: {
          sx: getTooltipStyles()
        }
      },
      ...rest,
      children: /* @__PURE__ */ jsx("div", { onClick: handleTooltipState(!open), children: disableHoverListener ? overwritenChildren : children })
    }
  ) });
};
Tooltip.propTypes = {
  arrow: PropTypes.bool,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  content: PropTypes.node.isRequired,
  disableHoverListener: PropTypes.bool,
  disableTouchListener: PropTypes.bool,
  enterDelay: PropTypes.number,
  leaveDelay: PropTypes.number,
  size: PropTypes.string,
  testId: PropTypes.string
};
export {
  Dropdown as D,
  HamburgerButton as H,
  IconRenderer as I,
  LinkResolver as L,
  NavigationLink as N,
  OfferCriteria as O,
  ProviderHighlights as P,
  SearchIcon as S,
  TextField as T,
  Logo as a,
  OfferCta as b,
  OfferLogo as c,
  Skeleton as d,
  SourceIdLink as e,
  TextField$1 as f,
  Tooltip as g
};
//# sourceMappingURL=Tooltip-BVewPeS5.js.map
